/**
 * Contractor portal accounts (wave O batch 2, item 1): the dialog that creates one.
 *
 * The server returns the temporary password ONCE, in the create response. It lives in
 * this dialog's own state only: it is rendered on the spot, never put in a toast, the
 * console or the URL, and closing the dialog (which unmounts it) discards it for good.
 * With a real email transport (wave COM item 4) the answer carries no password
 * (`handover` EMAIL): the dialog then says in words that it was emailed to the address.
 */

import { Check, Copy, LoaderCircle, UserPlus } from "lucide-react";
import { useState } from "react";
import { useOutletContext } from "react-router";

import { ApiError } from "../api/client";
import type { Me } from "../api/me";
import {
  assignPortalUserContracts,
  createPortalUser,
  listContracts,
  listPortalUsers,
  readPortalAccounts,
  revokePortalUserContract,
  type PasswordHandover,
  type PortalUser,
} from "../api/portfolio";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { PICKER_CLASS, PickerOption, pickerWidth } from "./PickerOption";
import { ActionDialog } from "@/components/ui/action-dialog";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Field, Refusal } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { day } from "../screens/parts";
import { useQuery, type QueryState } from "../screens/useQuery";
import { ServerErrorScope, ServerField, refusalText } from "../lib/serverErrors";

const copy = strings.portalAccounts;

/**
 * The create affordance is the operator side's: the server holds the verb and refuses
 * every wrong write, so a contractor, inspector or management account is shown none.
 */
export function useMayCreatePortalAccount(): boolean {
  const outlet = useOutletContext<{ me: Me } | null>();
  const workflowClass = outlet?.me.workflowClass;
  return (
    outlet !== null &&
    workflowClass !== "contractor" &&
    workflowClass !== "tpi" &&
    workflowClass !== "management"
  );
}

/** What a parent may learn of a created account: never the password. */
export interface CreatedAccount {
  readonly personId: string;
  readonly username: string;
  readonly fullName: string;
}

export function CreatePortalAccountDialog({
  partyId,
  partyName,
  onCreated,
  onClose,
}: {
  readonly partyId: string;
  readonly partyName: string;
  /** Fires once the account exists; the parent never receives the password. */
  readonly onCreated?: (account: CreatedAccount) => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const token = useToken();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [created, setCreated] = useState<{
    fullName: string;
    username: string;
    email: string;
    password: string | null;
    handover: PasswordHandover;
  } | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  const incomplete = fullName.trim() === "" || email.trim() === "" || username.trim() === "";

  const submit = async (): Promise<void> => {
    if (busy || incomplete) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await createPortalUser(token, partyId, {
        fullName: fullName.trim(),
        email: email.trim(),
        username: username.trim(),
      });
      setCreated({
        fullName: result.fullName,
        username: result.username,
        email: result.email,
        password: result.temporaryPassword,
        handover: result.handover,
      });
      onCreated?.({
        personId: result.personId,
        username: result.username,
        fullName: result.fullName,
      });
    } catch (caught: unknown) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const copyPassword = async (): Promise<void> => {
    if (created?.password == null) {
      return;
    }
    try {
      await navigator.clipboard.writeText(created.password);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  };

  const refusal =
    error === null
      ? null
      : error instanceof ApiError && error.kind === "absence"
        ? copy.absent
        : refusalText(error, {
            full_name: copy.fieldLabels.full_name,
            email: copy.fieldLabels.email,
            username: copy.fieldLabels.username,
          });

  return (
    <ActionDialog open title={copy.createHeading(partyName)} onClose={onClose}>
      {created === null ? (
        <ServerErrorScope error={error}>
          <div className="space-y-3" data-testid="portal-account-form">
            <Field>
              <Label htmlFor="portal-account-name">{copy.fullNameLabel}</Label>
              <ServerField field="full_name">
                <Input
                  id="portal-account-name"
                  data-testid="portal-account-name"
                  value={fullName}
                  onChange={(event) => {
                    setFullName(event.target.value);
                  }}
                />
              </ServerField>
            </Field>
            <Field>
              <Label htmlFor="portal-account-email">{copy.emailLabel}</Label>
              <ServerField field="email">
                <Input
                  id="portal-account-email"
                  data-testid="portal-account-email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                />
              </ServerField>
            </Field>
            <Field>
              <Label htmlFor="portal-account-username">{copy.usernameLabel}</Label>
              <ServerField field="username">
                <Input
                  id="portal-account-username"
                  data-testid="portal-account-username"
                  value={username}
                  onChange={(event) => {
                    setUsername(event.target.value);
                  }}
                />
              </ServerField>
            </Field>
            <Refusal message={refusal} />
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                {copy.cancel}
              </Button>
              <Button
                data-testid="portal-account-submit"
                disabled={busy || incomplete}
                aria-busy={busy}
                onClick={() => {
                  void submit();
                }}
              >
                {busy && <LoaderCircle className="animate-spin" aria-hidden="true" />}
                {busy ? copy.submitBusy : copy.submit}
              </Button>
            </DialogFooter>
          </div>
        </ServerErrorScope>
      ) : created.handover === "EMAIL" && created.password === null ? (
        <div className="space-y-3" data-testid="portal-account-done">
          <p className="text-base font-semibold">{copy.doneHeading}</p>
          <p className="text-sm" data-testid="portal-account-emailed">
            {copy.doneLineEmailed(created.fullName, created.username, created.email)}
          </p>
          <p className="text-sm" data-testid="portal-account-change-note">
            {copy.changeNote}
          </p>
          <DialogFooter>
            <Button data-testid="portal-account-close" onClick={onClose}>
              {copy.doneEmailed}
            </Button>
          </DialogFooter>
        </div>
      ) : (
        <div className="space-y-3" data-testid="portal-account-done">
          <p className="text-base font-semibold">{copy.doneHeading}</p>
          <p className="text-sm">{copy.doneLine(created.fullName, created.username)}</p>
          <div className="space-y-1">
            <Label htmlFor="portal-account-password">{copy.passwordLabel}</Label>
            <div className="flex items-center gap-2">
              <code
                id="portal-account-password"
                data-testid="portal-account-password"
                className="rounded-md border border-border bg-muted px-3 py-2 font-mono text-base select-all"
              >
                {created.password ?? ""}
              </code>
              <Button
                variant="outline"
                data-testid="portal-account-copy"
                onClick={() => {
                  void copyPassword();
                }}
              >
                {copyState === "copied" ? (
                  <Check aria-hidden="true" />
                ) : (
                  <Copy aria-hidden="true" />
                )}
                {copy.copy}
              </Button>
            </div>
            {copyState === "copied" && (
              <p className="text-sm" role="status" data-testid="portal-account-copied">
                {copy.copied}
              </p>
            )}
            {copyState === "failed" && (
              <p
                className="text-sm text-danger"
                role="alert"
                data-testid="portal-account-copy-failed"
              >
                {copy.copyFailed}
              </p>
            )}
          </div>
          <p className="text-sm" data-testid="portal-account-change-note">
            {copy.changeNote}
          </p>
          <p className="text-sm font-medium" data-testid="portal-account-once-note">
            {copy.onceNote}
          </p>
          <p className="text-xs text-muted-foreground" data-testid="portal-account-delivery-note">
            {copy.deliveryNote}
          </p>
          <DialogFooter>
            <Button data-testid="portal-account-close" onClick={onClose}>
              {copy.done}
            </Button>
          </DialogFooter>
        </div>
      )}
    </ActionDialog>
  );
}

/** The "Create portal account" button and its dialog, for a contractor party. */
export function CreatePortalAccountButton({
  partyId,
  partyName,
  onCreated,
}: {
  readonly partyId: string;
  readonly partyName: string;
  readonly onCreated?: (account: CreatedAccount) => void;
}): React.JSX.Element | null {
  const may = useMayCreatePortalAccount();
  const [open, setOpen] = useState(false);
  if (!may) {
    return null;
  }
  return (
    <>
      <Button
        variant="outline"
        data-testid="portal-account-open"
        onClick={() => {
          setOpen(true);
        }}
      >
        <UserPlus aria-hidden="true" />
        {copy.createButton}
      </Button>
      {open && (
        <CreatePortalAccountDialog
          partyId={partyId}
          partyName={partyName}
          {...(onCreated === undefined ? {} : { onCreated })}
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

/**
 * The registration form's account branch for an EXISTING contractor: its connected
 * accounts, one to assign to the project's contract, or a new account made inline (the
 * same dialog) which is then the pick. The pick is the person's id; no secret is kept.
 */
export function PartyAccountPicker({
  partyId,
  partyName,
  value,
  onChange,
}: {
  readonly partyId: string;
  readonly partyName: string;
  readonly value: string;
  readonly onChange: (personId: string, name: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const [stamp, setStamp] = useState(0);
  const [justCreated, setJustCreated] = useState<string | null>(null);
  const accounts = useQuery(
    (signal) => listPortalUsers(token, partyId, { signal }),
    [token, partyId, stamp],
  );
  return (
    <fieldset className="flex flex-col gap-2" data-testid="party-account-picker">
      <legend className="mb-1 text-sm tracking-[0.04em] text-muted-foreground">
        {copy.pickLegend}
      </legend>
      <p className="text-sm text-muted-foreground">{copy.pickIntro}</p>
      {accounts.kind === "loading" && <p className="text-sm">{copy.pickLoading}</p>}
      {(accounts.kind === "failed" || accounts.kind === "absence") && (
        <p className="text-sm text-danger" role="alert" data-testid="party-account-failed">
          {copy.pickFailed}
        </p>
      )}
      {accounts.kind === "ready" && accounts.data.length === 0 && (
        <p className="text-sm" data-testid="party-account-empty">
          {copy.pickEmpty}
        </p>
      )}
      {accounts.kind === "ready" && accounts.data.length > 0 && (
        <RadioGroup
          value={value}
          onValueChange={(next) => {
            onChange(next, accounts.data.find((one) => one.personId === next)?.name ?? "");
          }}
        >
          <Label className="min-h-11 cursor-pointer text-base tracking-normal text-foreground">
            <RadioGroupItem value="" data-testid="party-account-none" />
            {copy.pickNone}
          </Label>
          {accounts.data.map((one) => (
            <Label
              key={one.personId}
              className="min-h-11 cursor-pointer text-base tracking-normal text-foreground"
            >
              <RadioGroupItem value={one.personId} data-testid={`party-account-${one.username}`} />
              {copy.accountLine(one.name, one.username)}
            </Label>
          ))}
        </RadioGroup>
      )}
      {justCreated !== null && (
        <p className="text-sm" role="status" data-testid="party-account-created">
          {copy.pickCreatedLine(justCreated)}
        </p>
      )}
      <div>
        <CreatePortalAccountButton
          partyId={partyId}
          partyName={partyName}
          onCreated={(account) => {
            setJustCreated(account.fullName);
            onChange(account.personId, account.fullName);
            setStamp((one) => one + 1);
          }}
        />
      </div>
    </fieldset>
  );
}

/* ---------------------------------------------------------------------- */
/* Item 3: the connected accounts with their assigned contracts.           */
/* ---------------------------------------------------------------------- */

function problemText(error: unknown): string {
  return error instanceof ApiError && error.kind === "absence" ? copy.absent : refusalText(error);
}

function RevokeDialog({
  partyId,
  account,
  contractId,
  contractLabel,
  onDone,
  onClose,
}: {
  readonly partyId: string;
  readonly account: PortalUser;
  readonly contractId: string;
  readonly contractLabel: string;
  readonly onDone: () => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const token = useToken();
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const confirm = async (): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    try {
      await revokePortalUserContract(token, partyId, account.personId, contractId);
      onDone();
    } catch (caught: unknown) {
      // Wave S: the server answers 404 to anyone who may not revoke this contract (and
      // when the assignment is already gone); say so in plain words, not "not found".
      setRefusal(
        caught instanceof ApiError && caught.kind === "absence"
          ? copy.revokeAbsent
          : refusalText(caught),
      );
      setBusy(false);
    }
  };
  return (
    <ActionDialog open title={copy.revokeHeading} onClose={onClose}>
      <p className="text-base" data-testid="portal-revoke-words">
        {copy.revokeConfirm(account.name, contractLabel)}
      </p>
      <Refusal message={refusal} />
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {copy.cancel}
        </Button>
        <Button
          data-testid="portal-revoke-confirm"
          disabled={busy}
          aria-busy={busy}
          onClick={() => {
            void confirm();
          }}
        >
          {busy ? copy.revokeBusy : copy.revokeSubmit}
        </Button>
      </DialogFooter>
    </ActionDialog>
  );
}

function AssignRow({
  partyId,
  account,
  options,
  onDone,
}: {
  readonly partyId: string;
  readonly account: PortalUser;
  readonly options: readonly { id: string; humanId: string; title: string | null }[];
  readonly onDone: (message: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const [contractId, setContractId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const free = options.filter((one) => !account.assignedContracts.some((a) => a.id === one.id));
  if (free.length === 0) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid={`portal-assign-none-${account.username}`}
      >
        {copy.assignNone}
      </p>
    );
  }
  const submit = async (): Promise<void> => {
    const chosen = free.find((one) => one.id === contractId);
    if (chosen === undefined || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await assignPortalUserContracts(token, partyId, account.personId, { contractId });
      setContractId("");
      onDone(copy.assignDone(account.name, chosen.humanId));
    } catch (caught: unknown) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };
  return (
    <ServerErrorScope error={error}>
      <div className="flex flex-wrap items-end gap-2">
        <Field className="min-w-[14rem] flex-1">
          <Label htmlFor={`portal-assign-${account.username}`}>{copy.assignLabel}</Label>
          <ServerField field="contract_id">
            <NativeSelect
              id={`portal-assign-${account.username}`}
              data-testid={`portal-assign-${account.username}`}
              className={PICKER_CLASS}
              value={contractId}
              onChange={(event) => {
                setContractId(event.target.value);
              }}
            >
              <option value="">{copy.assignChoose}</option>
              {free.map((one) => (
                <PickerOption
                  key={one.id}
                  value={one.id}
                  identifier={one.humanId}
                  name={one.title}
                  width={pickerWidth(free.map((c) => c.humanId))}
                />
              ))}
            </NativeSelect>
          </ServerField>
        </Field>
        <Button
          variant="outline"
          data-testid={`portal-assign-submit-${account.username}`}
          disabled={busy || contractId === ""}
          aria-busy={busy}
          onClick={() => {
            void submit();
          }}
        >
          {busy ? copy.assignBusy : copy.assignButton}
        </Button>
      </div>
      <Refusal message={error === null ? null : problemText(error)} />
    </ServerErrorScope>
  );
}

/**
 * The connected accounts of one contractor, each with its assigned contracts. Holders
 * of the verb assign and revoke here; everyone else who can read the list sees it plain.
 *
 * `projectId` (the project bible, Wave S): the list is the server's for that project
 * only, each account carrying just its assignments on the project's contracts, and the
 * contracts offered for assignment are the project's. The party's other accounts are
 * listed beneath, so one with nothing on this project can still be given a contract.
 * `mayRevoke` is the server's word on revoking (the project detail's
 * `can_revoke_portal_contracts`) and `mayAssign` on assigning (its
 * `can_assign_portal_contracts`, Wave Experience A). Without them (the evaluations
 * dossier) the list's own words decide (`can_assign_portal_contracts`,
 * `can_revoke_portal_contracts`), and each assignment's `revocable_by_me` decides its
 * own revoke button. The persona rule is only the fallback for a server that says nothing.
 */
export function PortalAccountsBlock(props: {
  readonly partyId: string;
  readonly partyName: string;
  readonly projectId?: string | null;
  readonly mayRevoke?: boolean;
  readonly mayAssign?: boolean;
}): React.JSX.Element | null {
  // Only inside the signed-in shell, where the persona is known; nowhere else.
  const outlet = useOutletContext<{ me: Me } | null>();
  return outlet === null ? null : <PortalAccountsInner {...props} />;
}

function PortalAccountsInner({
  partyId,
  partyName,
  projectId = null,
  mayRevoke,
  mayAssign,
}: {
  readonly partyId: string;
  readonly partyName: string;
  readonly projectId?: string | null;
  readonly mayRevoke?: boolean;
  readonly mayAssign?: boolean;
}): React.JSX.Element | null {
  const token = useToken();
  const persona = useMayCreatePortalAccount();
  const [stamp, setStamp] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<{
    account: PortalUser;
    id: string;
    label: string;
  } | null>(null);
  const read = useQuery(
    (signal) => readPortalAccounts(token, partyId, { projectId, signal }),
    [token, partyId, projectId, stamp],
  );
  // The server's words first (the page's, then the list's); the persona rule only when the
  // server said nothing (an older server).
  const listWords = read.kind === "ready" ? read.data : null;
  const may = mayAssign ?? listWords?.canAssign ?? persona;
  const revokes = mayRevoke ?? listWords?.canRevoke ?? persona;
  const accounts: QueryState<readonly PortalUser[]> =
    read.kind === "ready" ? { kind: "ready", data: read.data.accounts } : read;
  // On the bible only, and only for the assign affordance: the party's accounts with
  // nothing on this project, which the project-filtered list omits by design.
  const everyAccount = useQuery(
    (signal) =>
      projectId !== null && may
        ? listPortalUsers(token, partyId, { signal })
        : Promise.resolve([] as PortalUser[]),
    [token, partyId, projectId, may, stamp],
  );
  const contracts = useQuery(
    (signal) => listContracts(token, { projectId, signal }),
    [token, projectId],
  );
  if (accounts.kind === "absence" || accounts.kind === "failed") {
    // The list is the operator's own read: absence for anyone else draws nothing.
    return null;
  }
  const options =
    contracts.kind === "ready"
      ? contracts.data.items
          .filter((one) => one.contractorPartyId === partyId)
          .map((one) => ({ id: one.id, humanId: one.humanId, title: null as string | null }))
      : [];
  const onProject = accounts.kind === "ready" ? accounts.data.map((one) => one.personId) : [];
  const otherAccounts =
    projectId !== null && may && accounts.kind === "ready" && everyAccount.kind === "ready"
      ? everyAccount.data.filter((one) => !onProject.includes(one.personId))
      : [];
  return (
    <section className="space-y-3" data-testid={`portal-accounts-${partyId}`}>
      <h4 className="text-sm font-semibold">
        {copy.blockHeading}: {partyName}
      </h4>
      <p className="text-sm text-muted-foreground">{copy.blockLede}</p>
      {accounts.kind === "loading" && <p className="text-sm">{strings.common.loading}</p>}
      {accounts.kind === "ready" && accounts.data.length === 0 && (
        <p className="text-sm" data-testid="portal-accounts-empty">
          {projectId === null ? copy.blockEmpty : copy.blockEmptyOnProject}
        </p>
      )}
      {accounts.kind === "ready" &&
        accounts.data.map((account) => (
          <div
            key={account.personId}
            className="space-y-2 rounded-md border border-border p-3"
            data-testid={`portal-account-${account.username}`}
          >
            <p className="text-base font-medium">
              {copy.accountLine(account.name, account.username)}
            </p>
            {account.createdAt !== null && (
              <p className="text-xs text-muted-foreground">
                {copy.createdLine(account.createdByName, day(account.createdAt))}
              </p>
            )}
            <p className="text-sm font-medium">{copy.assignedLabel}</p>
            {account.assignedContracts.length === 0 ? (
              <p className="text-sm text-muted-foreground">{copy.noContracts}</p>
            ) : (
              <ul className="space-y-1">
                {account.assignedContracts.map((one) => (
                  <li key={one.id} className="flex flex-wrap items-center gap-2 text-sm">
                    <span>{copy.contractLine(one.humanId, one.title)}</span>
                    {(one.revocableByMe ?? revokes) && (
                      <Button
                        variant="ghost"
                        data-testid={`portal-revoke-${account.username}-${one.humanId}`}
                        onClick={() => {
                          setNotice(null);
                          setRevoking({ account, id: one.id, label: one.humanId });
                        }}
                      >
                        {copy.revokeButton(one.humanId)}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {may && (
              <AssignRow
                partyId={partyId}
                account={account}
                options={options}
                onDone={(message) => {
                  setNotice(message);
                  setStamp((one) => one + 1);
                }}
              />
            )}
          </div>
        ))}
      {otherAccounts.length > 0 && (
        <div className="space-y-2" data-testid="portal-accounts-others">
          <p className="text-sm font-medium">{copy.othersHeading}</p>
          <p className="text-sm text-muted-foreground">{copy.othersLede}</p>
          {otherAccounts.map((account) => (
            <div
              key={account.personId}
              className="space-y-2 rounded-md border border-border p-3"
              data-testid={`portal-other-account-${account.username}`}
            >
              <p className="text-base font-medium">
                {copy.accountLine(account.name, account.username)}
              </p>
              <AssignRow
                partyId={partyId}
                account={account}
                options={options}
                onDone={(message) => {
                  setNotice(message);
                  setStamp((one) => one + 1);
                }}
              />
            </div>
          ))}
        </div>
      )}
      {notice !== null && (
        <p className="text-sm" role="status" data-testid="portal-accounts-notice">
          {notice}
        </p>
      )}
      <p className="text-xs text-muted-foreground" data-testid="portal-accounts-history-note">
        {copy.historyNote}
      </p>
      <CreatePortalAccountButton
        partyId={partyId}
        partyName={partyName}
        onCreated={() => {
          setStamp((one) => one + 1);
        }}
      />
      {revoking !== null && (
        <RevokeDialog
          partyId={partyId}
          account={revoking.account}
          contractId={revoking.id}
          contractLabel={revoking.label}
          onDone={() => {
            setNotice(copy.revokeDone(revoking.account.name, revoking.label));
            setRevoking(null);
            setStamp((one) => one + 1);
          }}
          onClose={() => {
            setRevoking(null);
          }}
        />
      )}
    </section>
  );
}
